import { SearchableSelect } from "@/components/ui/searchable-select";
import { RecordProblem } from "@/components/patterns";
import { useCallRecord } from "@/components/calling/CallLink";
import React, { useEffect, useState, useCallback } from "react";
import { useRecordCrumb } from "@/components/shell/ShellContext";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { WhatsAppChatDialog } from "@/components/whatsapp/WhatsAppChatDialog";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { StatusTabs } from "@/components/patterns";
import { ActivityLog } from "@/components/customer/ActivityLog";
import { DealsWidget } from "@/components/entity-detail/DealsWidget";
import { QuotesWidget } from "@/components/entity-detail/QuotesWidget";
import { ProposalsWidget } from "@/components/entity-detail/ProposalsWidget";
import { AgreementsWidget } from "@/components/entity-detail/AgreementsWidget";
import { unwrapPage } from "@/lib/paging";
import type { AgreementListRow } from "@/types/agreement";
import { EmailsWidget } from "@/components/entity-detail/EmailsWidget";
import { WhatsAppWidget } from "@/components/entity-detail/WhatsAppWidget";
import { ActivityTimeline } from "@/components/entity-detail/ActivityTimeline";
import { AccountRecordHeader } from "./record/AccountRecordHeader";
import { AccountProperties } from "./record/AccountProperties";
import { CustomerLocation } from "@/components/visits/CustomerLocation";
import { AccountRelated } from "./record/AccountRelated";
import { INDIAN_STATES, stateFromGstin } from "@/lib/indianStates";
import type { Account360 } from "@/types";
import type { Task } from "@/types/task";
import type { WhatsAppThread } from "@/types/whatsapp";

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();
  const { hasPermission } = useAuth();
  const callRecord = useCallRecord();

  const [data, setData] = useState<Account360 | null>(null);
  useRecordCrumb(data?.account?.name);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("activity");
  const [agreements, setAgreements] = useState<AgreementListRow[]>([]);
  useEffect(() => {
    if (!id || !hasPermission("agreement.read")) return;
    api
      .get(`/api/Agreement/List?account_id=${id}&page_size=50`)
      .then((res) => setAgreements(unwrapPage<AgreementListRow>(res).rows))
      .catch(() => setAgreements([]));
  }, [id]);

  // Dialogs
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    industry: "",
    website: "",
    email: "",
    phone: "",
    billing_address: "",
    shipping_address: "",
    state: "",
    gstin: "",
    notes: "",
  });
  const [editLoading, setEditLoading] = useState(false);

  // WhatsApp Chat Dialog
  const [waDialogOpen, setWaDialogOpen] = useState(false);
  const [waPhone, setWaPhone] = useState("");
  const [waName, setWaName] = useState("");

  // Quick Task Dialog
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);

  // Add Contact Dialog
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    mobile: "",
    job_title: "",
    is_primary: false,
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);

  const [problem, setProblem] = useState<unknown>(null);
  const fetch360 = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setProblem(null);
      const res = await api.get(`/api/Account/360/${id}`);
      const payload = unwrapObject<Account360>(res);
      setData(payload);
      if (payload?.account) {
        setEditForm({
          name: payload.account.name || "",
          industry: payload.account.industry || "",
          website: payload.account.website || "",
          email: payload.account.email || "",
          phone: payload.account.phone || "",
          billing_address: payload.account.billing_address || "",
          shipping_address: payload.account.shipping_address || "",
          state: payload.account.state || "",
          gstin: payload.account.gstin || "",
          notes: payload.account.notes || "",
        });
      }
    } catch (err: any) {
      setProblem(err);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toastError]);

  useEffect(() => {
    fetch360();
  }, [fetch360]);

  if (problem) return <RecordProblem error={problem} what="account" record={{ type: "ACCOUNT", id }} backTo="/accounts" backLabel="Back to accounts" onRetry={fetch360} />;

  if (loading || !data) {
    return (
      <div className="p-6 space-y-6">
        <FormSkeleton />
      </div>
    );
  }

  const { account, contacts, deals, quotations, emails, whatsapp, tasks } = data;
  const proposals = data.proposals || [];
  const canSeeAgreements = hasPermission("agreement.read");
  const history = data.history || [];

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      setEditLoading(true);
      await api.put(`/api/Account/Update/${id}`, editForm);
      success("Account updated", "Account details have been successfully saved.");
      setEditOpen(false);
      fetch360();
    } catch (err: any) {
      toastError("Update failed", err.message || "Could not update account.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleAddContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      setContactSubmitting(true);
      await api.post("/api/Contact/Insert", {
        ...contactForm,
        account_id: id,
      });
      success("Contact added", "New contact has been linked to this account.");
      setAddContactOpen(false);
      setContactForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        mobile: "",
        job_title: "",
        is_primary: false,
      });
      fetch360();
    } catch (err: any) {
      toastError("Failed to add contact", err.message || "Could not create contact.");
    } finally {
      setContactSubmitting(false);
    }
  };

  const openWhatsApp = (phone: string, name?: string) => {
    setWaPhone(phone);
    setWaName(name || account.name);
    setWaDialogOpen(true);
  };

  const doneTask = async (t: Task) => {
    try {
      await api.patch(`/api/Task/Complete/${t.id}`, { outcome: "Completed from the account" });
      fetch360();
    } catch (err: any) {
      toastError("Could not complete the task", err.message || "Try again");
    }
  };

  const openChat = (t: WhatsAppThread) => openWhatsApp(t.conversation.wa_id, t.conversation.profile_name || undefined);
  const timeline = (
    <ActivityTimeline deals={deals} quotations={quotations} emails={emails} whatsapp={whatsapp} tasks={tasks} proposals={proposals} history={history} onOpenWhatsApp={openChat} />
  );

  const tabItems = [
    { key: "activity", label: "Activity" },
    { key: "timeline", label: "Timeline" },
    { key: "deals", label: "Deals", count: deals.length },
    { key: "quotes", label: "Quotes", count: quotations.length },
    { key: "proposals", label: "Proposals", count: proposals.length },
    ...(canSeeAgreements ? [{ key: "agreements", label: "Agreements", count: agreements.length }] : []),
    { key: "emails", label: "Emails", count: emails.length },
    { key: "whatsapp", label: "WhatsApp", count: whatsapp.length },
  ];

  return (
    <>
      <div className="flex flex-col gap-3">
        <AccountRecordHeader
          data={data}
          can={{ update: hasPermission("account.update"), whatsapp: hasPermission("whatsapp.send"), deal: hasPermission("deal.create") }}
          onCall={() => callRecord("ACCOUNT", account.id, account.phone)}
          onWhatsApp={() => openWhatsApp(account.phone!, account.name)}
          onEmail={() => window.open(`mailto:${account.email}`)}
          onEdit={() => setEditOpen(true)}
          onNewDeal={() => navigate(`/deals?create=true&account_id=${account.id}&account_name=${encodeURIComponent(account.name)}`)}
        />

        <div className="grid grid-cols-1 items-start gap-3 @4xl/main:grid-cols-12">
          <div className="order-2 min-w-0 space-y-3 @4xl/main:order-1 @4xl/main:col-span-4 @6xl/main:col-span-3">
            <AccountProperties account={account} />
            <CustomerLocation key={account.id} type="ACCOUNT" id={account.id} lat={(account as any).geo_lat} lng={(account as any).geo_lng} setAt={(account as any).geo_set_at} canEdit={hasPermission("account.update") && hasPermission("visit.create")} />
          </div>
          <div className="order-1 flex min-w-0 flex-col gap-3 @4xl/main:order-2 @4xl/main:col-span-8 @6xl/main:col-span-6">
            <StatusTabs tabs={tabItems} active={activeTab} onChange={setActiveTab} label="Account sections" />
            {activeTab === "activity" && <ActivityLog relatedType="ACCOUNT" relatedId={account.id} canCreate={hasPermission("activity.create")} />}
            {activeTab === "timeline" && timeline}
            {activeTab === "deals" && (
              <DealsWidget
                deals={deals}
                onNewDeal={() => navigate(`/deals?create=true&account_id=${account.id}&account_name=${encodeURIComponent(account.name)}`)}
                canCreate={hasPermission("deal.create")}
              />
            )}
            {activeTab === "quotes" && <QuotesWidget quotes={quotations} canCreate={hasPermission("quote.create")} />}
            {activeTab === "proposals" && <ProposalsWidget proposals={proposals} />}
            {activeTab === "agreements" && canSeeAgreements && <AgreementsWidget agreements={agreements} />}
            {activeTab === "emails" && <EmailsWidget emails={emails} canSend={hasPermission("email.send")} />}
            {activeTab === "whatsapp" && (
              <WhatsAppWidget
                threads={whatsapp}
                onOpenChat={openChat}
                onStartChat={account.phone ? () => openWhatsApp(account.phone!, account.name) : undefined}
                canSend={hasPermission("whatsapp.send")}
              />
            )}
          </div>
          <div className="order-3 min-w-0 @4xl/main:col-span-12 @6xl/main:col-span-3">
            <AccountRelated
              accountId={account.id}
              contacts={contacts}
              deals={deals}
              tasks={tasks}
              can={{ contact: hasPermission("contact.create"), task: hasPermission("activity.create"), merge: hasPermission("account.merge") }}
              onAddContact={() => setAddContactOpen(true)}
              onTask={() => setTaskDialogOpen(true)}
              onDoneTask={doneTask}
              onMerged={fetch360}
            />
          </div>
        </div>
      </div>

      {/* Edit Account Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <form onSubmit={handleEditSubmit}>
            <DialogHeader>
              <DialogTitle>Edit Account</DialogTitle>
              <DialogDescription>
                Update company profile, industry, contacts, and tax information.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3.5 py-4 text-xs sm:text-sm">
              <div className="space-y-1.5">
                <Label htmlFor="acc_name">Company / Organization Name *</Label>
                <Input
                  id="acc_name"
                  value={editForm.name}
                  onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="acc_industry">Industry</Label>
                  <Input
                    id="acc_industry"
                    value={editForm.industry}
                    onChange={(e) => setEditForm((p) => ({ ...p, industry: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="acc_website">Website</Label>
                  <Input
                    id="acc_website"
                    placeholder="company.com"
                    value={editForm.website}
                    onChange={(e) => setEditForm((p) => ({ ...p, website: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="acc_email">Corporate Email</Label>
                  <Input
                    id="acc_email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="acc_phone">Main Phone</Label>
                  <Input
                    id="acc_phone"
                    value={editForm.phone}
                    onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="acc_gstin">GSTIN</Label>
                  <Input
                    id="acc_gstin"
                    placeholder="27AABCU9603R1ZX"
                    value={editForm.gstin}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase();
                      setEditForm((p) => ({ ...p, gstin: v, state: stateFromGstin(v) || p.state }));
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="acc_state">State</Label>
                  <SearchableSelect
                    id="acc_state"
                    className="w-full h-9 sm:h-10 rounded-md border border-input bg-background px-3 text-xs sm:text-sm"
                    value={editForm.state}
                    onChange={(e) => setEditForm((p) => ({ ...p, state: e.target.value }))}
                  >
                    <option value="">-- Select State --</option>
                    {INDIAN_STATES.map((st) => (
                      <option key={st.code} value={st.name}>
                        {st.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="acc_billing">Billing Address</Label>
                <Input
                  id="acc_billing"
                  value={editForm.billing_address}
                  onChange={(e) => setEditForm((p) => ({ ...p, billing_address: e.target.value }))}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="acc_notes">Notes</Label>
                <textarea
                  id="acc_notes"
                  rows={3}
                  className="w-full rounded-md border border-input bg-background p-2.5 text-xs sm:text-sm"
                  value={editForm.notes}
                  onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={editLoading}>
                {editLoading ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={addContactOpen} onOpenChange={setAddContactOpen}>
        <DialogContent className="sm:max-w-lg">
          <form onSubmit={handleAddContactSubmit}>
            <DialogHeader>
              <DialogTitle>Add Contact to {account.name}</DialogTitle>
              <DialogDescription>
                Add a new stakeholder or representative for this account.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3.5 py-4 text-xs sm:text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cnt_first">First Name *</Label>
                  <Input
                    id="cnt_first"
                    value={contactForm.first_name}
                    onChange={(e) => setContactForm((p) => ({ ...p, first_name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cnt_last">Last Name</Label>
                  <Input
                    id="cnt_last"
                    value={contactForm.last_name}
                    onChange={(e) => setContactForm((p) => ({ ...p, last_name: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="cnt_title">Job Title / Designation</Label>
                <Input
                  id="cnt_title"
                  placeholder="e.g. Procurement Manager"
                  value={contactForm.job_title}
                  onChange={(e) => setContactForm((p) => ({ ...p, job_title: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cnt_email">Email</Label>
                  <Input
                    id="cnt_email"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cnt_phone">Mobile / WhatsApp</Label>
                  <Input
                    id="cnt_phone"
                    value={contactForm.mobile}
                    onChange={(e) => setContactForm((p) => ({ ...p, mobile: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="cnt_primary"
                  checked={contactForm.is_primary}
                  onChange={(e) => setContactForm((p) => ({ ...p, is_primary: e.target.checked }))}
                  className="rounded border-input text-primary focus:ring-primary size-4"
                />
                <Label htmlFor="cnt_primary" className="cursor-pointer text-xs font-medium">
                  Set as Primary Contact for this Account
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddContactOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={contactSubmitting}>
                {contactSubmitting ? "Adding..." : "Add Contact"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Chat Dialog */}
      {waPhone && (
        <WhatsAppChatDialog
          open={waDialogOpen}
          onOpenChange={(v) => {
            setWaDialogOpen(v);
            if (!v) fetch360();
          }}
          phone={waPhone}
          name={waName}
        />
      )}

      {/* Task Dialog */}
      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        defaultRelated={{
          type: "ACCOUNT",
          id: account.id,
          name: account.name,
        }}
        onSaved={() => {
          setTaskDialogOpen(false);
          fetch360();
        }}
      />
    </>
  );
}
